import { LitElement, html, css, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";

import "@material/web/button/filled-button.js";
import "@material/web/button/text-button.js";
import "@material/web/textfield/outlined-text-field.js";
import "@material/web/icon/icon.js";

import { sharedStyles } from "../styles/shared";
import "../components/app-shell";
import { inputValue, errorMessage } from "../lib/dom";
import {
  browserSupportsPasskeys,
  listPasskeys,
  registerPasskey,
  deletePasskey,
  loadSignInMethods,
  type PasskeyCredential,
  type SignInMethods,
} from "../lib/signin-methods";

// Where somebody adds a passkey to their own account.
//
// This matters most for the accounts that can approve a production batch: those
// are the ones worth phishing, because signing in as one of them is the cheapest
// way to have genuine tags minted. A passkey is bound to this origin by the
// browser, so a convincing lookalike domain cannot use it.

@customElement("security-page")
export class SecurityPage extends LitElement {
  static styles = [
    sharedStyles,
    css`
      .keys {
        display: grid;
        gap: 8px;
        margin-top: 16px;
      }
      .key {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 12px 14px;
        border: 1px solid var(--md-sys-color-outline-variant);
        border-radius: 12px;
      }
      .key .meta {
        flex: 1;
        min-width: 0;
      }
      .key .name {
        font-weight: 600;
      }
      .key .sub {
        font-size: 0.85rem;
        color: var(--md-sys-color-on-surface-variant);
      }
      .add {
        display: flex;
        gap: 8px;
        align-items: end;
        margin-top: 16px;
      }
      .add md-outlined-text-field {
        flex: 1;
      }
      .empty {
        color: var(--md-sys-color-on-surface-variant);
        font-size: 0.95rem;
      }
      .unavailable {
        background: var(--md-sys-color-surface-container);
        border-radius: 12px;
        padding: 14px 16px;
        font-size: 0.9rem;
        color: var(--md-sys-color-on-surface-variant);
      }
    `,
  ];

  @state() private keys: PasskeyCredential[] = [];
  @state() private label = "";
  @state() private busy = false;
  @state() private error = "";
  @state() private notice = "";
  @state() private methods: SignInMethods | null = null;

  connectedCallback(): void {
    super.connectedCallback();
    void this.refresh();
  }

  private async refresh(): Promise<void> {
    this.methods = await loadSignInMethods();
    this.keys = await listPasskeys();
  }

  private add(): void {
    void (async () => {
      this.error = "";
      this.notice = "";
      this.busy = true;
      try {
        await registerPasskey(this.label || defaultLabel());
        this.label = "";
        this.notice = "Passkey added.";
        await this.refresh();
      } catch (err) {
        this.error = errorMessage(err, "Could not add that passkey.");
      } finally {
        this.busy = false;
      }
    })();
  }

  // NOT named "remove": HTMLElement already has one, and shadowing it makes the
  // element unusable in ways the type checker reports at the decorator.
  private removeKey(id: string): void {
    void (async () => {
      this.error = "";
      this.busy = true;
      try {
        await deletePasskey(id);
        await this.refresh();
      } catch (err) {
        this.error = errorMessage(err, "Could not remove that passkey.");
      } finally {
        this.busy = false;
      }
    })();
  }

  render() {
    const supported = browserSupportsPasskeys();
    const enabled = this.methods?.passkey === true;
    return html`
      <app-shell active="/security">
        <section class="page">
          <header class="page-header">
            <h1>Security</h1>
            <p class="subtitle">
              Passkeys sign you in with your device instead of a password. They
              cannot be used on a copycat site, which makes them the safest
              option for accounts that can approve production batches.
            </p>
          </header>

          ${
          !enabled
            ? html`<div class="unavailable">
                Passkeys are not enabled on this deployment.
              </div>`
            : !supported
              ? html`<div class="unavailable">
                  This browser does not support passkeys. Try a current version
                  of Safari, Chrome, Edge or Firefox.
                </div>`
              : html`
                  <div class="add">
                    <md-outlined-text-field
                      label="Name this device (optional)"
                      .value=${this.label}
                      @input=${(e: Event) => (this.label = inputValue(e))}
                    ></md-outlined-text-field>
                    <md-filled-button
                      ?disabled=${this.busy}
                      @click=${() => this.add()}
                    >
                      ${this.busy ? "Waiting…" : "Add a passkey"}
                    </md-filled-button>
                  </div>
                `
        }
          ${this.error ? html`<p class="error-text">${this.error}</p>` : nothing}
          ${this.notice ? html`<p class="subtitle">${this.notice}</p>` : nothing}

          <div class="keys">
            ${
            this.keys.length === 0
              ? html`<p class="empty">
                  No passkeys yet. Your password and one-time codes keep working
                  either way.
                </p>`
              : this.keys.map(
                  (k) => html`
                    <div class="key">
                      <md-icon>key</md-icon>
                      <div class="meta">
                        <div class="name">${k.label || "Passkey"}</div>
                        <div class="sub">
                          ${
                          k.lastUsedAt
                            ? `Last used ${formatDate(k.lastUsedAt)}`
                            : "Never used"
                        }${k.backedUp ? " · synced" : ""}
                        </div>
                      </div>
                      <md-text-button
                        ?disabled=${this.busy}
                        @click=${() => this.removeKey(k.id)}
                      >
                        Remove
                      </md-text-button>
                    </div>
                  `,
                )
          }
          </div>
        </section>
      </app-shell>
    `;
  }
}

function defaultLabel(): string {
  const ua = navigator.userAgent;
  if (/iPhone|iPad/.test(ua)) return "iPhone";
  if (/Android/.test(ua)) return "Android device";
  if (/Mac/.test(ua)) return "Mac";
  if (/Windows/.test(ua)) return "Windows PC";
  return "This device";
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "recently" : d.toLocaleDateString();
}

declare global {
  interface HTMLElementTagNameMap {
    "security-page": SecurityPage;
  }
}
